import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Check, CheckCircle2, ChevronDown, Clapperboard, Loader2, MapPin, Navigation } from 'lucide-react';
import { API_BASE } from '@/config/api';
import { PreviewPane } from './CallSheetPage';
import { cn } from '@/lib/utils';

/**
 * The crew link (M3.2): /cs/:token. No login. The person's own call first,
 * a Confirm button, then the full sheet. Phone-first — this is read on a
 * bus at 5am.
 */
export default function CallSheetPublic() {
  const { token } = useParams();
  const [view, setView] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/public/call-sheets/${encodeURIComponent(token)}`)
      .then((r) => { if (!cancelled) setView(r.data); })
      .catch((err) => {
        if (cancelled) return;
        const s = err?.response?.status;
        setError(s === 410 ? 'This call sheet link has expired.' : s === 404 ? 'This link is not valid.' : 'Could not load the call sheet. Please try again.');
      });
    return () => { cancelled = true; };
  }, [token]);

  const confirm = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API_BASE}/public/call-sheets/${encodeURIComponent(token)}/confirm`);
      setView((v) => ({ ...v, me: { ...v.me, status: 'confirmed', confirmed_at: new Date().toISOString() } }));
    } catch {
      setError('Could not confirm — please try again or tell your AD.');
    } finally {
      setConfirming(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">{children}</div>
    </div>
  );

  if (error) {
    return <Shell><div className="rounded-2xl border border-border p-8 text-center text-sm text-muted-foreground">{error}</div></Shell>;
  }
  if (!view) {
    return <Shell><div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="size-5 animate-spin" /></div></Shell>;
  }

  const me = view.me || {};
  const confirmed = me.status === 'confirmed';

  return (
    <Shell>
      <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-2">
        <Clapperboard className="size-3.5" /> Call sheet{view.version > 1 ? ` · v${view.version}` : ''} · {view.film_title}
      </div>

      {/* My call */}
      <section className="rounded-3xl bg-foreground text-background p-5 space-y-3" data-testid="cs-my-call">
        <div>
          <div className="text-xs opacity-70">{me.name}{me.role ? ` · ${me.role}` : ''}</div>
          <div className="text-5xl font-bold tracking-tight leading-none mt-1">{me.call_time || view.general_call || '—'}</div>
          <div className="text-sm opacity-80 mt-1">Your call · {view.shoot_date_text || 'date to be confirmed'}</div>
        </div>
        {(me.pickup_time || me.scenes?.length > 0 || me.notes) && (
          <div className="text-sm space-y-0.5 opacity-90">
            {me.pickup_time && <div>Pickup <b>{me.pickup_time}</b></div>}
            {me.scenes?.length > 0 && <div>Your scenes: <b>{me.scenes.join(', ')}</b></div>}
            {me.notes && <div>{me.notes}</div>}
          </div>
        )}
        {view.location_name && (
          <div className="flex items-start gap-2 text-sm">
            <MapPin className="size-4 mt-0.5 shrink-0 opacity-70" />
            <div className="min-w-0"><div className="font-semibold">{view.location_name}</div>{view.location_address && <div className="opacity-80">{view.location_address}</div>}</div>
            {view.map_url && (
              <a href={view.map_url} target="_blank" rel="noreferrer" className="ml-auto shrink-0 inline-flex items-center gap-1 rounded-full bg-background/15 px-3 py-1 text-xs font-semibold"><Navigation className="size-3" /> Map</a>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={confirm}
          disabled={confirming || confirmed}
          className={cn('w-full h-12 rounded-2xl font-semibold text-base inline-flex items-center justify-center gap-2 transition-colors',
            confirmed ? 'bg-green-500 text-white' : 'bg-primary text-primary-foreground hover:opacity-90')}
          data-testid="cs-confirm"
        >
          {confirming ? <Loader2 className="size-5 animate-spin" /> : confirmed ? <><CheckCircle2 className="size-5" /> Confirmed — see you on set</> : <><Check className="size-5" /> Confirm I've seen this</>}
        </button>
      </section>

      <div className="grid grid-cols-3 gap-2 text-center">
        {[['Crew call', view.crew_call], ['Shooting', view.shooting_call], ['Est. wrap', view.est_wrap]].map(([l, v]) => (
          <div key={l} className="rounded-xl bg-muted px-2 py-2"><div className="text-lg font-bold leading-tight">{v || '—'}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div></div>
        ))}
      </div>

      {view.nearest_hospital && <div className="text-sm rounded-xl border border-border p-3"><span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-2">Nearest hospital</span>{view.nearest_hospital}</div>}
      {view.notes && <div className="rounded-xl bg-muted p-3 text-sm whitespace-pre-wrap">{view.notes}</div>}

      <button type="button" onClick={() => setShowAll((v) => !v)} className="w-full flex items-center justify-center gap-1 text-sm text-muted-foreground py-2" data-testid="cs-show-all">
        {showAll ? 'Hide full call sheet' : `Full call sheet — ${view.scenes.length} scenes, ${view.recipient_count} people`} <ChevronDown className={cn('size-4 transition-transform', showAll && 'rotate-180')} />
      </button>
      {showAll && <PreviewPane view={view} compact />}

      <footer className="pt-6 pb-4 text-center text-xs text-muted-foreground">
        {view.watermark ? (
          <a href="https://frameflow.cinerian.com/?utm_source=callsheet&utm_medium=crewlink" className="hover:text-foreground">
            <b>Made with FrameFlow</b> — plan your shoot free at frameflow.cinerian.com
          </a>
        ) : <span>Sent with FrameFlow</span>}
        <div className="mt-1 opacity-70">This link is personal to you and expires a few days after the shoot.</div>
      </footer>
    </Shell>
  );
}
